import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Image, Pressable, StyleSheet } from 'react-native';
import {
  DNABox,
  DNAButton,
  DNAText,
  Iffy,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';

import FileUpload, { ManagedFile } from 'src/components/DNA/FileUpload/FileUpload';
import { getImageFromCloudfront } from 'src/utils/loadCloudfrontAsset/common';
import TenantLogo from 'src/components/DNA/Header/TenantLogo';

const styles = StyleSheet.create({
  logoContainer: {
    backgroundColor: colors['color-gray-10'],
    borderColor: colors['color-gray-80'],
    borderRadius: 8,
    borderWidth: 1,
    height: 137,
    width: 300,
  },
  logoPlaceholder: {
    height: 36,
    width: 36,
  },
  logoWrapper: {
    height: 100,
    width: 200,
  },
});

export enum TENANT_LOGO_VARIANCE {
  PRIMARY = 'PRIMARY',
  HUB = 'HUB'
}

const VARIANCE_SETTINGS = {
  [TENANT_LOGO_VARIANCE.PRIMARY]: {
    dimensions: 'Max dimensions: 98 x 32 pixels',
    fileName: 'logo.png',
    title: 'Primary logo',
  },
  [TENANT_LOGO_VARIANCE.HUB]: {
    dimensions: 'Max dimensions: 300 x 40 pixels',
    fileName: 'logo-hub.png',
    title: 'Hub logo',
  },
};

const FileUploadWrapper: React.FC<PropsWithChildren<{
  onImageSelected: (url: string) => void,
  onFinish: (url: ManagedFile) => void,
}>> = (props) => {
  return (
    <FileUpload>
      {
        (fileUpload) => (
          <>
            {
              fileUpload.files?.map((file) => (
                <FileUpload.RowProgress
                  key={`${file.key}`}
                  file={file}
                  onFinished={(url) => {
                    props.onFinish(url);
                  }}
                >
                  {
                    (e) => (
                      <DNABox appearance="col">
                        <Iffy is={e.progressPercent > 0 && e.progressPercent !== 100}>
                          <DNABox fill style={{ marginVertical: 4 }}>
                            <FileUpload.ProgressBar
                              progress={e.progressPercent}
                              finalValue={100}
                            />
                          </DNABox>
                        </Iffy>
                      </DNABox>
                    )
                  }
                </FileUpload.RowProgress>
              ))
            }

            <FileUpload.Input
              s3PathPrefix="tenant_tmp/"
              fileExt={['png']}
              processOnSelect
              onSelect={(files) => {
                const [file] = files as unknown as File[];
                if (file) {
                  props.onImageSelected(URL.createObjectURL(file));
                }
              }}
            >
              {props.children}
            </FileUpload.Input>
          </>
        )
      }
    </FileUpload>
  )
}

const TenantFileUploadLogo: React.FC<{
  onImageSelected: (url: string) => void,
  onFinish: (url: ManagedFile) => void,
  image: string | undefined,
}> = (props) => {
  return (<FileUploadWrapper
    onImageSelected={props.onImageSelected}
    onFinish={props.onFinish}
  >
    <Pressable style={styles.logoContainer}>
      <DNABox fill appearance="col" alignY="center" alignX="center">
        <Iffy is={props.image}>
          <DNABox style={styles.logoWrapper}>
            <TenantLogo size="medium" imageUrl={props.image} />
          </DNABox>
        </Iffy>
        <Iffy is={!props.image}>
          <Image
            style={styles.logoPlaceholder}
            source={require('../../../../../assets/images/ImagePlaceholder.svg')}
          />
        </Iffy>
      </DNABox>
    </Pressable>
  </FileUploadWrapper>
  )
}

const TenantFileUploadBtn: React.FC<{
  onImageSelected: (url: string) => void,
  onFinish: (url: ManagedFile) => void,
}> = (props) => {
  return (
    <FileUploadWrapper
      onImageSelected={props.onImageSelected}
      onFinish={props.onFinish}
    >
      <DNAButton
        appearance="outline"
        status="tertiary"
        iconLeft="upload-simple"
        children="Upload"
      />
    </FileUploadWrapper>
  )
}

const UploadImageTenant: React.FC<{
  tenantId: string,
  onFinishHandler?: (file: ManagedFile, variance: TENANT_LOGO_VARIANCE) => void,
  removeImage?: (variance: TENANT_LOGO_VARIANCE) => void,
  variance: TENANT_LOGO_VARIANCE,
}> = (props) => {
  const [image, setImage] = useState<string | undefined>(undefined);

  useEffect(() => {
    const init = async () => {
      const path = `${props.tenantId}/${VARIANCE_SETTINGS[props.variance].fileName}`;
      const img = await getImageFromCloudfront(path);
      const isBase64Img = img?.startsWith('data:image/png;base64');

      if (isBase64Img) {
        setImage(img);
      }
    }

    init();
  }, [props.tenantId, props.variance]);

  const imageSelectedHandler = (image: string | undefined) => {
    setImage(image);
    !image && props?.removeImage?.(props.variance);
  }

  const onFinishHandler = (file: ManagedFile) => {
    props.onFinishHandler && props.onFinishHandler(file, props.variance);
  }

  return (
    <DNABox fill appearance="col" spacing="md">
      <DNABox appearance="col" spacing="sm">
        <DNAText>{VARIANCE_SETTINGS[props.variance].title}</DNAText>
        <TenantFileUploadLogo image={image} onImageSelected={imageSelectedHandler} onFinish={onFinishHandler} />
        <DNAText status="subtle">{VARIANCE_SETTINGS[props.variance].dimensions}</DNAText>
      </DNABox>
      <DNABox appearance="row" spacing="sm">
        <TenantFileUploadBtn onImageSelected={imageSelectedHandler} onFinish={onFinishHandler} />
        <Iffy is={image}>
          <DNAButton
            appearance="outline"
            status="tertiary"
            iconLeft="trash"
            onPress={() => imageSelectedHandler(undefined)}
            children="Remove"
          />
        </Iffy>
      </DNABox>
    </DNABox>
  )
}

export default React.memo(UploadImageTenant);
